import type { components } from '@hive/api';

type S = components['schemas'];
export type LicenseStatus = S['hive.core.license.Licensing.Status'];
export type LicenseKey = S['hive.core.license.Licensing.Key'];

/** Rota da tela de licença (Configurações → Licença). */
export const LICENSE_ROUTE = '/settings/license';

export type BannerTone = 'info' | 'warn' | 'crit';

/** Faixa de aviso da console: chave do texto, tom e valores. Sem aviso (licença em dia) → null. */
export interface LicenseBanner {
  tone: BannerTone;
  key:
    | 'license.banner.demo'
    | 'license.banner.trial'
    | 'license.banner.grace'
    | 'license.banner.expired'
    | 'license.banner.unlicensed'
    | 'license.banner.full'
    | 'license.banner.near';
  vars: { days?: number; used?: number; limit?: number };
}

/**
 * O que a faixa mostra, do mais grave ao mais leve: só leitura (teste vencido ou produção sem licença), carência do
 * teste, limite de agentes atingido, 90% do limite, dias restantes do teste e o selo de demonstração.
 */
export function licenseBanner(s: LicenseStatus | null | undefined): LicenseBanner | null {
  if (!s) return null;
  const used = s.used ?? 0;
  const limit = s.limit ?? undefined;
  const days = s.daysLeft ?? 0;
  if (s.state === 'expired') return { tone: 'crit', key: 'license.banner.expired', vars: {} };
  if (s.state === 'unlicensed') return { tone: 'crit', key: 'license.banner.unlicensed', vars: {} };
  if (s.state === 'grace') return { tone: 'crit', key: 'license.banner.grace', vars: { days } };
  if (s.full && limit != null) return { tone: 'crit', key: 'license.banner.full', vars: { used, limit } };
  if (s.warn && limit != null) return { tone: 'warn', key: 'license.banner.near', vars: { used, limit } };
  if (s.state === 'trial') return { tone: 'info', key: 'license.banner.trial', vars: { days } };
  if (s.state === 'demo') return { tone: 'info', key: 'license.banner.demo', vars: {} };
  return null;
}

/** Tom da barra de uso: crítico no limite, atenção a partir de 90%. */
export function usageTone(s: LicenseStatus): 'ok' | 'warn' | 'crit' {
  if (s.full) return 'crit';
  if (s.warn) return 'warn';
  return 'ok';
}

/** Tom do selo de cada chave. */
export function keyTone(state: string | undefined): 'ok' | 'warn' | 'crit' | 'neutral' | 'info' {
  switch (state) {
    case 'active':
      return 'ok';
    case 'pending':
      return 'info';
    case 'expired':
      return 'crit';
    default:
      return 'neutral';
  }
}

/** Serial colado com quebras de linha e espaços (e-mail, PDF): o núcleo aceita, mas a prévia mostra limpo. */
export function cleanSerial(v: string): string {
  return v.replace(/\s+/g, '');
}

/** Parece um serial do gerador (HIVE1.payload.assinatura)? Só para habilitar o botão; quem confere é o núcleo. */
export function looksLikeSerial(v: string): boolean {
  return /^HIVE1\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(cleanSerial(v));
}
